import type { CountryCode } from 'libphonenumber-js'
import { regionToCountry } from 'v2/configs/constants'
import { queryClient } from 'v2/configs/rq-setup'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RUserProfile } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import dayjs from 'v2/utils/dayjs'
import { logger } from 'v2/utils/logger'

export const DATE_TIME_FORMAT = 'MMM D, YYYY h:mm A'
export const DATE_FORMAT = 'MMM D, YYYY'
export const TIME_FORMAT = 'HH:mm:ss'

export const getAgentTimezone = () => {
  const profile = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
  if (
    !profile ||
    !profile.organizations ||
    !profile.organizations[0] ||
    !profile.organizations[0].timezone
  )
    return 'UTC'
  return profile.organizations[0].timezone
}

// The org's default country (ISO-2), derived from the deployment region, used as
// the fallback region when normalizing a bare national phone number the user
// searches CDRs by. Returns undefined for an unknown region so callers can skip
// normalization rather than guess wrong.
export const getAgentCountry = (): CountryCode | undefined => {
  const iso = regionToCountry(getLocalAuth()?.region)
  return iso.length === 2 ? (iso as CountryCode) : undefined
}

export const formatDateTime = (
  dateTime: Date | number | string,
  {
    format = 'MMM DD, YYYY hh:mm:ss A',
    utc = false,
    useAgentTimezone = true,
  }: { format?: string; utc?: boolean; useAgentTimezone?: boolean } = {},
): string => {
  let dayjsValue = dayjs(dateTime)
  if (!dayjsValue.isValid()) {
    logger.warn(`Invalid date provided to formatDateTime: ${dateTime}`)
    return '-'
  }

  if (utc) {
    dayjsValue = dayjsValue.utc()
  } else if (useAgentTimezone) {
    const agentTimezone = getAgentTimezone()
    dayjsValue = dayjs.utc(dateTime).tz(agentTimezone)
  }

  return dayjsValue.format(format)
}
